/**
 * Left-hand showcase for every screen in the Auth shell: an auto-rotating
 * carousel of Alloy's main product areas. Each slide pairs an illustration
 * with the module's name, a headline and one line on what it does,
 * crossfading between slides with progress dots underneath.
 *
 * Presentation only. The designer's prototype fills the illustrations from
 * its sample-data stores; the product has no such stores and shows no
 * fabricated figures, so the illustrations here are decorative and carry no
 * numbers, names or identifiers. The whole stage is aria-hidden.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import {
  ArrowLeftRight,
  Check,
  CheckCircle,
  Coins,
  FileText,
  Fingerprint,
  Layers,
  Shield,
  ShieldCheck,
  Sparkles,
  Wallet,
  type LucideIcon,
} from "lucide-react";
import { BrandLockup } from "@/components/shell/brand-logo";
import { AuthFit } from "@/components/shell/auth-fit";
import { cn } from "@/lib/utils";

const SLIDE_MS = 6000;

type Slide = {
  id: string;
  tag: string;
  TagIcon: LucideIcon;
  title: string;
  body: string;
  Illustration: () => React.ReactElement;
};

const SLIDES: Slide[] = [
  {
    id: "wallets",
    tag: "Wallets",
    TagIcon: Wallet,
    title: "Every custody provider, one console",
    body: "Run wallets from your existing custodians and MPC providers side by side, under one policy engine and one audit trail.",
    Illustration: ProviderHub,
  },
  {
    id: "transfers",
    tag: "Transfers",
    TagIcon: ArrowLeftRight,
    title: "Screened before anything moves",
    body: "Each transfer is screened by your compliance screening partner and checked against policy before it is signed and broadcast.",
    Illustration: TransferExecuting,
  },
  {
    id: "approvals",
    tag: "Approvals",
    TagIcon: CheckCircle,
    title: "Quorum approvals for high-value moves",
    body: "Treasury movements route to a 2-of-3 quorum. Approvers see the matched policy and sign with a passkey.",
    Illustration: ApprovalQuorum,
  },
  {
    id: "governance",
    tag: "Governance",
    TagIcon: Shield,
    title: "Policies that evaluate every request",
    body: "Velocity limits, recipient allowlists and screening thresholds hold or block a request before it can sign.",
    Illustration: PolicyEvaluations,
  },
  {
    id: "digital-assets",
    tag: "Stablecoins & Tokenized Assets",
    TagIcon: Coins,
    title: "Digital assets across every partner",
    body: "Track USDC, USDT, EURC and tokenized funds across networks and partners, reconciled provider by provider.",
    Illustration: StablecoinHoldings,
  },
  {
    id: "evidence",
    tag: "Evidence",
    TagIcon: FileText,
    title: "An evidence packet for every decision",
    body: "Transfers, approvals, recoveries and screenings each leave an exportable record for audit and compliance.",
    Illustration: EvidencePacket,
  },
];

export function AuthShowcase() {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const timer = useRef<number | null>(null);

  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const sync = () => setReducedMotion(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  const autoplay = !paused && !reducedMotion;

  useEffect(() => {
    if (!autoplay) return;
    timer.current = window.setTimeout(() => setActive((i) => (i + 1) % SLIDES.length), SLIDE_MS);
    return () => {
      if (timer.current) window.clearTimeout(timer.current);
    };
  }, [active, autoplay]);

  const pause = useCallback(() => setPaused(true), []);
  const resume = useCallback(() => setPaused(false), []);

  return (
    <section
      className="auth-showcase"
      aria-roledescription="carousel"
      aria-label="Alloy features"
      onMouseEnter={pause}
      onMouseLeave={resume}
      onFocus={pause}
      onBlur={resume}
    >
      <AuthFit>
        <div className="auth-showcase__slides">
          {SLIDES.map(({ id, tag, TagIcon, title, body, Illustration }, i) => (
            <div
              key={id}
              className={cn("auth-showcase__slide", i === active && "is-active")}
              role="group"
              aria-roledescription="slide"
              aria-label={`${i + 1} of ${SLIDES.length}: ${tag}`}
              aria-hidden={i !== active}
            >
              <div className="auth-showcase__stage" aria-hidden="true">
                <Illustration />
              </div>
              <div className="auth-showcase__copy">
                <span className="auth-showcase__tag">
                  <TagIcon size={14} strokeWidth={2} aria-hidden />
                  {tag}
                </span>
                <h2>{title}</h2>
                <p>{body}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="auth-showcase__dots">
          {SLIDES.map(({ id, tag }, i) => (
            <button
              key={id}
              type="button"
              className={cn("auth-showcase__dot", i === active && "is-active", autoplay && "is-playing")}
              aria-label={`Show ${tag}`}
              aria-current={i === active}
              onClick={() => setActive(i)}
            >
              <span style={{ animationDuration: `${SLIDE_MS}ms` }} />
            </button>
          ))}
        </div>
      </AuthFit>
    </section>
  );
}

/* ── Illustrations ───────────────────────────────────────────────────────
   Built from HTML + tokens rather than a raster so they follow the theme.
   Every node is decorative (the stage is aria-hidden). */

function Node({
  children,
  className,
  style,
}: {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}) {
  return (
    <span className={cn("sc-node", className)} style={style}>
      {children}
    </span>
  );
}

// Node rows on the hub's 520×280 canvas; the wires and the nodes share them.
const ROWS = [60, 140, 220];
const HUB_W = 520;
const HUB_H = 280;
const RAIL_Y = 140;
const NODE_L_X = 70;
const NODE_R_X = 450;
// Fallback used only for the first paint, before the pill has been measured.
// Close to the real pill edges so there is no visible jump on hydration.
const DEFAULT_CORE = { left: 185, right: 335 };

/**
 * Wire endpoints are derived from the logo pill's measured edges rather than
 * hardcoded. The pill is sized by its content (the lockup plus padding), so a
 * fixed endpoint either overshoots or leaves the wires visibly short of the
 * logo — which reads as a dropped connection. A rail also runs at full width
 * behind the pill so every wire appears to pass *through* Alloy instead of
 * stopping at it.
 */
function useCoreEdges() {
  const hubRef = useRef<HTMLDivElement>(null);
  const coreRef = useRef<HTMLDivElement>(null);
  const [edges, setEdges] = useState(DEFAULT_CORE);

  useEffect(() => {
    const hub = hubRef.current;
    const core = coreRef.current;
    if (!hub || !core) return;

    const measure = () => {
      const hb = hub.getBoundingClientRect();
      const cb = core.getBoundingClientRect();
      if (!hb.width || !cb.width) return;
      // The wire SVG is inset:0 over the same box and stretches its 520×280
      // viewBox to fill, so viewBox units are just px scaled by 520 / width.
      const k = HUB_W / hb.width;
      setEdges({
        left: Math.max(0, (cb.left - hb.left) * k),
        right: Math.min(HUB_W, (cb.right - hb.left) * k),
      });
    };

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(hub);
    ro.observe(core);
    // The lockup is an inline SVG, so its width settles a frame after layout.
    const raf = requestAnimationFrame(measure);
    return () => {
      ro.disconnect();
      cancelAnimationFrame(raf);
    };
  }, []);

  return { hubRef, coreRef, edges };
}

function ProviderHub() {
  const left = [
    { label: "MPC", hint: "MPC provider" },
    { label: "CUS", hint: "Custodian" },
    { label: "ISS", hint: "Issuer" },
  ];
  const right = [Wallet, Coins, Layers];
  const { hubRef, coreRef, edges } = useCoreEdges();
  const { left: coreL, right: coreR } = edges;

  // The elbow (where a wire turns down onto the rail) is derived from the
  // measured pill edge too. The SVG stretches to fill the hub, so as the
  // viewport narrows the pill covers more viewBox units; a fixed elbow would
  // slide underneath the logo and the turn would vanish.
  const elbowL = Math.max(NODE_L_X + 30, coreL - 22);
  const elbowR = Math.min(NODE_R_X - 30, coreR + 22);

  // Each node runs a spine into the rail; the rail itself is one continuous
  // line behind the pill, so no wire ever dead-ends at the logo.
  const wires = [
    ...left.map((_, i) => ({ y: ROWS[i], x: NODE_L_X, elbow: elbowL, edge: coreL })),
    ...right.map((_, i) => ({ y: ROWS[i], x: NODE_R_X, elbow: elbowR, edge: coreR })),
  ].map(({ y, x, elbow, edge }) => {
    const dir = x < HUB_W / 2 ? 1 : -1;
    return {
      d: `M${x} ${y} H${elbow - dir * 20} Q${elbow} ${y} ${elbow} ${(y + RAIL_Y) / 2} V${RAIL_Y} H${edge}`,
      y,
      x,
    };
  });

  // Pulses stay on their own short leg: node -> rail on the way in, rail ->
  // node on the way out. Extending these across the whole rail tripled the
  // path length inside the same 2.4s duration, which read as fast dots. The
  // continuous rail keeps the wires looking continuous on its own.
  const pulsePath = `M${NODE_L_X} 60 H${elbowL - 20} Q${elbowL} 60 ${elbowL} 100 V${RAIL_Y} H${coreL}`;
  const pulsePath2 = `M${coreR} ${RAIL_Y} H${elbowR} V180 Q${elbowR} 220 ${elbowR + 20} 220 H${NODE_R_X}`;

  return (
    <div className="sc-hub" ref={hubRef}>
      <svg className="sc-hub__wires" viewBox={`0 0 ${HUB_W} ${HUB_H}`} preserveAspectRatio="none">
        {wires.map(({ d, y, x }) => (
          <path key={`${x}-${y}`} d={d} />
        ))}
        {/* The rail spans elbow to elbow — wider than the pill — so it is
            visible on both sides of the logo and hidden behind it, reading as
            one unbroken line passing through Alloy. */}
        <path className="sc-hub__rail" d={`M${elbowL} ${RAIL_Y} H${elbowR}`} />
        <circle className="sc-hub__pulse" r="3.5">
          <animateMotion dur="2.4s" repeatCount="indefinite" path={pulsePath} />
        </circle>
        <circle className="sc-hub__pulse" r="3.5">
          <animateMotion dur="2.4s" begin="-1.2s" repeatCount="indefinite" path={pulsePath2} />
        </circle>
      </svg>

      {left.map(({ label, hint }, i) => (
        <Node
          key={label}
          className={cn("sc-node--text", i === 0 && "sc-node--live")}
          style={{ left: `${(NODE_L_X / HUB_W) * 100}%`, top: `${(ROWS[i] / HUB_H) * 100}%` }}
        >
          <span title={hint}>{label}</span>
        </Node>
      ))}

      <div className="sc-hub__core" ref={coreRef}>
        <BrandLockup />
      </div>

      {right.map((Icon, i) => (
        <Node
          key={i}
          className={cn(i === 2 && "sc-node--live")}
          style={{ left: `${(NODE_R_X / HUB_W) * 100}%`, top: `${(ROWS[i] / HUB_H) * 100}%` }}
        >
          <Icon size={20} strokeWidth={1.75} />
        </Node>
      ))}
    </div>
  );
}

function TransferExecuting() {
  const steps = ["Compliance screening: low risk", "Policy checks passed", "Signed via your custody provider", "Broadcasting to Ethereum"];
  return (
    <div className="sc-exec">
      <span className="sc-exec__orb" />
      <span className="sc-exec__stem" />
      <div className="sc-exec__pill">
        <span className="sc-exec__spark">
          <Sparkles size={16} strokeWidth={2} />
        </span>
        Executing…
      </div>
      <ul className="sc-exec__steps">
        {steps.map((s, i) => (
          <li key={s} style={{ animationDelay: `${0.4 + i * 0.5}s` }} className={cn(i === steps.length - 1 && "is-current")}>
            <span>{i === steps.length - 1 ? <i /> : <Check size={11} strokeWidth={3} />}</span>
            {s}
          </li>
        ))}
      </ul>
    </div>
  );
}

function ApprovalQuorum() {
  const approvers = [
    { initials: "A1", done: true },
    { initials: "A2", done: true },
    { initials: "A3", done: false },
  ];
  return (
    <div className="sc-center">
      <div className="sc-card sc-approval">
        <div className="sc-row-between">
          <span className="sc-chip">
            <ArrowLeftRight size={12} strokeWidth={2} /> Transfer
          </span>
          <span className="sc-chip sc-chip--pending">Awaiting 1 approval</span>
        </div>
        <div className="sc-approval__amount">Treasury transfer</div>
        <div className="sc-approval__route">
          Treasury <span aria-hidden>→</span> Settlement
        </div>
        <div className="sc-approval__rows">
          <div>
            <ShieldCheck size={14} strokeWidth={2} /> Approval policy matched
          </div>
          <div>
            <Fingerprint size={14} strokeWidth={2} /> Passkey signature required
          </div>
        </div>
        <div className="sc-row-between">
          <div className="sc-approval__avatars">
            {approvers.map(({ initials, done }) => (
              <span key={initials} className={cn("sc-avatar", done && "is-done")}>
                {initials}
                {done && (
                  <i>
                    <Check size={9} strokeWidth={3} />
                  </i>
                )}
              </span>
            ))}
          </div>
          <span className="sc-approval__count">2 of 3 approved</span>
        </div>
        <div className="sc-bar">
          <span style={{ width: "66.6%" }} />
        </div>
      </div>
    </div>
  );
}

function PolicyEvaluations() {
  // Decorative proportions only: no counts are shown.
  const outcomes = [
    { label: "Allowed", weight: 8, color: "var(--ads-success)" },
    { label: "Held", weight: 2, color: "var(--ads-illo-amber)" },
    { label: "Blocked", weight: 1, color: "var(--ads-danger)" },
  ];
  const policies = ["Velocity limits", "Recipient allowlists", "Screening thresholds"];
  return (
    <div className="sc-center">
      <div className="sc-card sc-policy">
        <div className="sc-row-between">
          <span className="sc-muted">Policy evaluations</span>
          <span className="sc-chip sc-chip--live">Live</span>
        </div>
        <div className="sc-stack">
          {outcomes.map((o) => (
            <span key={o.label} style={{ flexGrow: o.weight, background: o.color }} />
          ))}
        </div>
        <div className="sc-legend">
          {outcomes.map((o) => (
            <span key={o.label}>
              <i style={{ background: o.color }} />
              {o.label}
            </span>
          ))}
        </div>
        <ul className="sc-policy__list">
          {policies.map((name) => (
            <li key={name}>
              <ShieldCheck size={14} strokeWidth={2} />
              <span>{name}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function StablecoinHoldings() {
  // Decorative mix only: symbols and proportions, no balances.
  const rows = [
    { sym: "USDC", weight: 5 },
    { sym: "USDT", weight: 3 },
    { sym: "EURC", weight: 2 },
    { sym: "Funds", weight: 1 },
  ];
  const colors = ["var(--ads-chart-1)", "var(--ads-chart-3)", "var(--ads-chart-7)", "var(--ads-chart-4)"];
  return (
    <div className="sc-center">
      <div className="sc-card sc-coins">
        <div className="sc-row-between">
          <span className="sc-muted">Digital assets</span>
          <span className="sc-chip">
            <Coins size={12} strokeWidth={2} /> By partner
          </span>
        </div>
        <div className="sc-stack">
          {rows.map((r, i) => (
            <span key={r.sym} style={{ flexGrow: r.weight, background: colors[i] }} />
          ))}
        </div>
        <ul className="sc-coins__list">
          {rows.map((r, i) => (
            <li key={r.sym}>
              <i style={{ background: colors[i] }} />
              <span>{r.sym}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function EvidencePacket() {
  const trail = ["Transfer requested", "Compliance screening", "Policy matched", "Dual approval recorded"];
  return (
    <div className="sc-center">
      <div className="sc-evidence">
        <span className="sc-evidence__sheet sc-evidence__sheet--2" />
        <span className="sc-evidence__sheet sc-evidence__sheet--1" />
        <div className="sc-card sc-evidence__card">
          <div className="sc-row-between">
            <span className="sc-chip">
              <FileText size={12} strokeWidth={2} /> Evidence packet
            </span>
            <span className="sc-chip sc-chip--success">Exportable</span>
          </div>
          <div className="sc-evidence__subject">Approved transfer</div>
          <div className="sc-muted">Every step recorded for audit</div>
          <ul className="sc-evidence__trail">
            {trail.map((t, i) => (
              <li key={t} style={{ animationDelay: `${0.3 + i * 0.35}s` }}>
                <span>
                  <Check size={10} strokeWidth={3} />
                </span>
                {t}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
